import React, { useEffect, useState } from "react";
import {
  Grid,
  TextField,
  MenuItem,
  Button,
  Typography,
  Box,
  IconButton,
  Divider,
  Autocomplete,
  alpha,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
} from "@mui/material";
import dayjs from "dayjs";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import CloseIcon from "@mui/icons-material/Close";
import * as XLSX from "xlsx";
import { callApi } from "../../../utils/apiClient";
import { Production_API_ENDPOINTS } from "../../../config/api/apiCallProduction";
import CustomTableContainer from "../../../components/table/CustomTableContainer";
import CustomPagination from "../../../components/table/CustomPagination";
import SkeletonTableLoader from "../../../components/table/SkeletonTableLoader";
import { tokens } from "../../../theme/theme";

// export default function RepulpSummaryReport({ handleBackWinderMachineEntry }) {
  
export default function RepulpEntryReport({ handleBackWinderMachineEntry }) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [filters, setFilters] = useState({
    fromDate: dayjs().startOf("month"),
    toDate: dayjs(),
    shiftId: "",
    locationId: "",
    machineId: "",
  });

  const [locations, setLocations] = useState([]);
  const [machineList, setMachineList] = useState([]);
  const [shiftList, setShiftList] = useState([]);
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [recordsPerPage, setRecordsPerPage] = useState(20);
  const [currentPage, setCurrentPage] = useState(1);

  // Load dropdowns
  const loadFilters = async () => {
    const companyId = parseInt(localStorage.getItem("companyid") || 0);
    const plantId = parseInt(localStorage.getItem("plantid") || 1);
    const userId = parseInt(localStorage.getItem("userID") || 0);
    const payload = { p_company_id: companyId, p_plant_id: plantId, p_created_by: userId };

    try {
      const locRes = await callApi(
        Production_API_ENDPOINTS.repulp.locationList,
        "POST",
        { "Content-Type": "application/json" },
        payload
      );
      setLocations(locRes?.data || locRes || []);

      const machineRes = await callApi(
        Production_API_ENDPOINTS.repulp.machineList,
        "POST",
        { "Content-Type": "application/json" },
        payload
      );
      setMachineList(machineRes?.data || machineRes || []);

      // Uncomment if shift dropdown is required
      // const shiftRes = await callApi(
      //   Production_API_ENDPOINTS.repulp.shiftList,
      //   "POST",
      //   payload
      // );
      // setShiftList(shiftRes?.data || shiftRes || []);
    } catch (error) {
      console.error("Error loading dropdowns:", error);
    }
  };

  useEffect(() => {
    loadFilters();
  }, []);

  const handleFilterChange = (field, value) => {
    setFilters({ ...filters, [field]: value });
  };

  const fetchReport = async () => {
    setLoading(true);
    const companyId = parseInt(localStorage.getItem("companyid") || 0);
    const plantId = parseInt(localStorage.getItem("plantid") || 1);
    const payload = {
      p_company_id: companyId,
      p_plant_id: plantId,
      p_from_date: dayjs(filters.fromDate).format("YYYY-MM-DD"),
      p_to_date: dayjs(filters.toDate).format("YYYY-MM-DD"),
      p_machine_id: filters.machineId || null,
      p_location_id: filters.locationId || null,
    };
    try {
      const res = await callApi(
        Production_API_ENDPOINTS.repulp.SummaryReport,
        "POST",
        { "Content-Type": "application/json" },
        payload
      );
      setReportData(res?.data || res || []);
      setCurrentPage(1);
    } catch (error) {
      console.error("Error fetching report:", error);
      setReportData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleExportToExcel = () => {
    const formattedData = reportData.map((row) => ({
      Date: row.entry_date,
      Location: row.location_name,
      Machine: row.machine_name,
      "Qty (KG)": row.quantity,
    }));
    const worksheet = XLSX.utils.json_to_sheet(formattedData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Repulp Summary Report");
    XLSX.writeFile(workbook, `Repulp_Summary_Report.xlsx`);
  };

  const pageCount = Math.ceil(reportData.length / recordsPerPage);
  const currentRecords = reportData.slice(
    (currentPage - 1) * recordsPerPage,
    currentPage * recordsPerPage
  );
  const handlePageChange = (event, value) => setCurrentPage(value);

  return (
    <Box p={2} sx={{ backgroundColor: theme.palette.background.default, minHeight: "100vh" }}>
      {/* Close Button at Top-Right */}
  <IconButton
    onClick={handleBackWinderMachineEntry}
    sx={{
      position: "absolute",
      top: 8,
      right: 8,
    
    }}
  >
    <CloseIcon />
  </IconButton>
        <Typography variant="h5" sx={{ mb: 2, fontWeight: "bold" }}>
              Repulp Entry Summary Report
            </Typography>
      <Divider />
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} sm={3}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="From Date"
              value={filters.fromDate}
              onChange={(date) => handleFilterChange("fromDate", date)}
              format="DD-MMM-YYYY"
            />
          </LocalizationProvider>
        </Grid>
        <Grid item xs={12} sm={3}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="To Date"
              value={filters.toDate}
              onChange={(date) => handleFilterChange("toDate", date)}
              format="DD-MMM-YYYY"
            />
          </LocalizationProvider>
        </Grid>
        <Grid item xs={12} sm={3}>
          <TextField
            select
            label="Location"
            fullWidth
            value={filters.locationId}
            onChange={(e) => handleFilterChange("locationId", e.target.value)}
          >
            <MenuItem value="">All</MenuItem>
            {locations.map((item) => (
              <MenuItem key={item.id} value={item.id}>
                {item.name}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} sm={3}>
          <TextField
            select
            label="Machine"
            fullWidth
            value={filters.machineId}
            onChange={(e) => handleFilterChange("machineId", e.target.value)}
          >
            <MenuItem value="">All</MenuItem>
            {machineList.map((item) => (
              <MenuItem key={item.id} value={item.id}>
                {item.name}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} sm={2}>
          <Button variant="contained" color="secondary" fullWidth onClick={fetchReport}>
            Show Report
          </Button>
        </Grid>
        <Grid item xs={12} sm={2}>
          <Button variant="contained" color="secondary" fullWidth onClick={handleExportToExcel}>
            Export to Excel
          </Button>
        </Grid>
       
      </Grid>

      <Divider sx={{ my: 2 }} />

      <CustomTableContainer colors={colors}>
        <TableContainer sx={{ maxHeight: "60vh", overflowY: "auto" }}>
          {loading ? (
            <SkeletonTableLoader rows={10} columns={4} />
          ) : reportData.length === 0 ? (
            <Typography sx={{ fontStyle: "italic", textAlign: "center", p: 2 }}>
              No data found. Please apply filters.
            </Typography>
          ) : (
            <Table size="small">
              <TableHead sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.1) }}>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Location</TableCell>
                  <TableCell>Machine</TableCell>
                  <TableCell align="right">Qty (KG)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {currentRecords.map((row, index) => (
                  <TableRow
                    key={index}
                    sx={{
                      backgroundColor:
                        index % 2 === 0
                          ? theme.palette.background.default
                          : alpha(theme.palette.action.hover, 0.04),
                    }}
                  >
                    <TableCell>{row.entry_date}</TableCell>
                    <TableCell>{row.location_name}</TableCell>
                    <TableCell>{row.machine_name}</TableCell>
                    <TableCell align="right">{row.quantity}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          <CustomPagination pageCount={pageCount} currentPage={currentPage} onPageChange={handlePageChange} />
        </TableContainer>
      </CustomTableContainer>
    </Box>
  );
}
